import { inject } from "@angular/core"
import { HttpRequest, HttpInterceptorFn, HttpErrorResponse } from "@angular/common/http"
import { throwError } from "rxjs"
import { catchError } from "rxjs/operators"
import { AuthService } from "../services/auth.service"

/** Our REST API (API Gateway). S3 and other AWS hosts are excluded. */
function isOurApiGatewayUrl(url: string): boolean {
  return url.includes("execute-api") && url.includes("amazonaws.com")
}

function extractHttpStatus(error: unknown): number | null {
  if (error instanceof HttpErrorResponse) {
    return error.status
  }
  if (error && typeof error === "object" && "status" in error) {
    const status = Number((error as { status: unknown }).status)
    return Number.isFinite(status) ? status : null
  }
  return null
}

function hasBearerAuthorization(request: HttpRequest<unknown>): boolean {
  const header = request.headers.get("Authorization") || ""
  return /^Bearer\s+\S+/i.test(header)
}

/**
 * Cognito authorizer failures return HTTP 401, but API Gateway often omits CORS
 * headers on that gateway response. The browser then hides the body from XHR and
 * Angular reports status 0 while DevTools still shows 401.
 *
 * Only treat status 0 as expiry for our API Gateway + Bearer, and only when online
 * (avoids logging out on true offline / generic network failure).
 */
function isSessionExpiryFailure(
  status: number | null,
  request: HttpRequest<unknown>,
): boolean {
  if (!hasBearerAuthorization(request) || !isOurApiGatewayUrl(request.url || "")) {
    return false
  }
  if (status === 401) {
    return true
  }
  // if (status === 0 && typeof navigator !== "undefined" && navigator.onLine) {
  //   return true
  // }
  return false
}

function attachAuthHeaders(
  request: HttpRequest<unknown>,
  authService: AuthService,
): HttpRequest<unknown> {
  const url = request.url || ""
  const isApiGateway = isOurApiGatewayUrl(url)

  // S3 and other non-API-Gateway AWS URLs: do not attach auth (callers may use x-skip-auth).
  if (url.includes("amazonaws.com") && !isApiGateway) {
    if (request.headers.has("x-skip-auth")) {
      return request.clone({
        headers: request.headers.delete("x-skip-auth"),
      })
    }
    return request
  }

  if (request.headers.has("x-skip-auth")) {
    return request.clone({
      headers: request.headers.delete("x-skip-auth"),
    })
  }

  const idToken = authService.getIdToken()
  if (!idToken) {
    return request
  }

  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${idToken}`,
    },
  })
}

/**
 * Global HTTP interceptor:
 * - Attaches Bearer idToken to API Gateway requests
 * - On session-invalid responses for those requests, clears session and redirects to /login
 */
export const tokenInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService)
  const modifiedRequest = attachAuthHeaders(request, authService)

  return next(modifiedRequest).pipe(
    catchError((error: unknown) => {
      const status = extractHttpStatus(error)

      if (isSessionExpiryFailure(status, modifiedRequest)) {
        authService.handleSessionExpired()
      }

      return throwError(() => error)
    }),
  )
}
